import './Game.scss'
import { Spinner, Stack } from "react-bootstrap"
import PlayerSection, { SECTIONS } from "./PlayerSection/PlayerSection"
import { useCallback, useEffect, useRef, useState } from "react"
import OpponentSection from "./OpponentSection/OpponentSection"
import FocusedCard from './FocusedCard'
import PlayerCounters from './PlayerCounters/PlayerCounters'
import Peer from 'peerjs'
import { v4 as uuidv4 } from 'uuid';
import History, { HISTORY_MESSAGE_TYPE } from './History/History'
import { defaultOptionsData, defaultPlayerData, getSavedOptions } from '../Home/Preferences/Preference'
import CardsLinks from './CarsLinks'
import ActionMode, { ACTION_MODES } from './ActionMode'
import DiceModal from './GameModals/DiceModal'
import HiddenSectionModal from './OpponentSection/HiddenSectionModal'
import { useGameContext } from '../Commons/GameContext'
import HelpModal from './GameModals/HelpModal'
import ExitModal from './GameModals/ExitModal'
import MultiplayerModal from './GameModals/MultiplayerModal'
import PreferenceModal from './GameModals/PreferenceModal'
import FormatPickerModal from './GameModals/FormatPickerModal'
import LeftBarButton from './GameElements/LeftBarButton'
import NewTurnMessage from './GameElements/NewTurnMessage'
import TurnOrderModal from './GameModals/TurnOrderModal'
import { displayOrderDebug, opponentDataDebug } from '../Commons/Functions'
import PlayerCountersOpponent from './PlayerCounters/PlayerCountersOpponent'
import PlayerCountersPlayer from './PlayerCounters/PlayerCountersPlayer'
import { useNavigate } from 'react-router'

const PING_INTERVAL = 5000; // toutes les 5s
const TIMEOUT_DURATION = 15000; // 15s sans réponse = inactif
const reconnectingPeers = new Set();
const disconnectingIds = new Set();

export default function Game() {
    const { gameData } = useGameContext()
    const isDebug = false
    const navigate = useNavigate();

    // MULTIPLAYER
    const peerInstance = useRef(null)
    const playersRef = useRef([])
    const playerDataRef = useRef({
        visibleCards: [],
        deck: [],
        deckTopCardHiddenTo: {},
        cardsLinks: [],
        profileData: { stillLoading: true },
        playerCounters: [
            {
                id: uuidv4(),
                value: 0
            },
            {
                id: uuidv4(),
                value: 0
            }
        ],
        playerTokens: {},
        playerNotes: "",
        opponentStackCards: []
    })
    const [playerData, setPlayerData] = useState(playerDataRef.current)
    const opponentsDataRef = useRef({})
    const [opponentsData, setOpponentsData] = useState({})
    const [stackOrder, setStackOrder] = useState(
        []
    )
    const commonDataRef = useRef({
        playerTurnOrder: [],
        history: [],
        stackOrder: [],
        turnCount: 0,
        currentPlayer: false
    })
    const [playerTurnOrder, setPlayerTurnOrder] = useState([])
    const [opponentsDisplayOrder, setOpponentsDisplayOrder] = useState([])

    // PANELS
    const [showMultiplayerPanel, setShowMultiplayerPanel] = useState(false)
    const [showPreferencePanel, setShowPreferencePanel] = useState(false)
    const [showTokensPanel, setShowTokensPanel] = useState(false)
    const [showDiceModal, setShowDiceModal] = useState(false)
    const [showHelpModal, setShowHelpModal] = useState(false)
    const [showExitModal, setShowExitModal] = useState(false)
    const [showFormatPickerModal, setShowFormatPickerModal] = useState(false)

    // GAME DATA
    const [history, setHistory] = useState([])
    const [turnCount, setTurnCount] = useState(0)
    const [gameOptions, setGameOptions] = useState(false)
    const [focusedCard, setFocusedCard] = useState(false)
    const [pings, setPings] = useState([])
    const [opponentStackCards, setOpponentStackCards] = useState([])
    const [givenCard, setGivenCard] = useState(false)
    const [hasPassed, setHasPassed] = useState(false)
    const [currentPlayer, setCurrentPlayer] = useState(false)
    const [cardsLinks, setCardsLinks] = useState([])
    const [allCardsLinks, setAllCardsLinks] = useState([])
    const [currentActionMode, setCurrentActionMode] = useState({
        action: ACTION_MODES.NONE,
        actionData: false,
        keys: {}
    })
    const [tokensAvailable, setTokensAvailable] = useState(false)
    const [options, setOptions] = useState(false)
    const cardToGiveBufferRef = useRef([])


    // OTHER
    const playerIdRef = useRef(null)
    const [toggleRemovePresented, setToggleRemovePresented] = useState(false)
    const stackRef = useRef(null)
    const isYourTurn = gameOptions && ((gameOptions.format.newTurn.sharedTurn) || (!gameOptions.format.newTurn.sharedTurn && currentPlayer == playerIdRef.current))
    const isActivePlayer = peerInstance.current && currentPlayer == playerIdRef.current
    const [isTextInputFocused, setIsTextInputFocused] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isEliminated, setIsEliminated] = useState(false);
    const [isPausingBeforeUpkeep, setIsPausingBeforeUpkeep] = useState({ val: false, lastTurn: -1 });
    const [isManualMode, setIsManualMode] = useState(false)
    const lastSeenMap = useRef({});
    const pendingCalls = useRef([])
    const [host, setHost] = useState(false)
    const [zoomedPlayer, setZoomedPlayer] = useState(-1)
    const preventReconnection = useRef(false)

    // CLOSE CALLBACKS
    const handleCloseMultiplayer = useCallback(() => {
        setShowMultiplayerPanel(false);
        setIsTextInputFocused(false);
    }, []);

    const handleClosePreference = useCallback(() => {
        setShowPreferencePanel(false);
        setIsTextInputFocused(false);
    }, []);

    const handleCloseDice = useCallback(() => {
        setShowDiceModal(false);
    }, []);

    const handleCloseHelp = useCallback(() => {
        setShowHelpModal(false);
    }, []);

    const handleCloseExit = useCallback(() => {
        setShowExitModal(false);
    }, []);

    const handleCloseFormatPicker = useCallback(() => {
        setShowFormatPickerModal(false);
    }, []);



    /*------------------------------- USE EFFECTS -------------------------------*/

    useEffect(() => {
        const savedUserOptions = getSavedOptions()
        setOptions(savedUserOptions)

        if (peerInstance.current == null) {
            iniPeer(savedUserOptions.playerId)
        }


        let profileJSON = localStorage.getItem('user_preference');
        let tmpPlayerData = { ...playerData }
        if (profileJSON) {
            const profileData = JSON.parse(profileJSON);
            tmpPlayerData.profileData = profileData
        } else {
            let defaultData = defaultPlayerData()
            localStorage.setItem('user_preference', JSON.stringify(defaultData));
            tmpPlayerData.profileData = defaultData
        }

        if (tmpPlayerData.profileData.background == "") {
            tmpPlayerData.profileData.background = gameData.defaultRessources.backgrounds[Math.floor(Math.random() * gameData.defaultRessources.backgrounds.length)];
            localStorage.setItem('user_preference', JSON.stringify(tmpPlayerData.profileData));
        }
        setPlayerData(tmpPlayerData)
        playerDataRef.current = tmpPlayerData

        const unload = async (e) => {
            closeCall()
        }
        const beforeUnload = async (e) => {
            e.preventDefault()
            return (e.returnValue = "Game progress will be lost.")
        }
        const handlePopState = (e) => {
            const confirmLeave = window.confirm("Do you really want to leave this page?");
            if (confirmLeave) {
                closeCall(); // User confirmed, trigger your custom logic
            } else {
                // User canceled, push state back to prevent leaving
                window.history.pushState(null, "", window.location.href);
            }
        };

        window.addEventListener('beforeunload', beforeUnload)
        window.addEventListener('pagehide', unload)
        window.history.pushState(null, "", window.location.href); // Add initial state
        window.addEventListener("popstate", handlePopState);

        const cardRotation = gameData.cardRotation ? gameData.cardRotation : 45
        document.documentElement.style.setProperty('--card-tapped-rotation', cardRotation + 'deg');

        // Heartbeat
        const interval = setInterval(() => {
            const now = Date.now();
            playersRef.current.forEach(p => {
                if (p.connection) {
                    const lastSeen = lastSeenMap.current[p.id];
                    if (lastSeen && now - lastSeen > TIMEOUT_DURATION) {
                        console.warn("Déconnexion détectée:", p.id);
                        // A REMETTRE QUAND JE COMPRENDRAIS POURQUOI CA MARCHE PAS
                        sendToHistory(`Is ${getPlayerName(p.id)} still online ?`, false, HISTORY_MESSAGE_TYPE.ERROR, true);
                        //aPlayerHasLeftTheGame(p.id);
                        attemptReconnect(p.id);
                        delete lastSeenMap.current[p.id]
                    }
                }
            });
        }, PING_INTERVAL);

        return () => {
            window.removeEventListener("beforeunload", beforeUnload);
            window.removeEventListener("pagehide", unload);
            window.removeEventListener("popstate", handlePopState);
            clearInterval(interval);
        };
    }, [])

    useEffect(() => {
        sendData({
            playerData: playerData
        })
        playerDataRef.current = playerData
    }, [playerData])

    useEffect(() => {
        opponentsDataRef.current = { ...opponentsData }
        let stackCards = []
        opponentsDisplayOrder.forEach((p) => {
            if (opponentsData[p]) {
                opponentsData[p].visibleCards.forEach((c) => {
                    if (c.position.section == SECTIONS.STACK) {
                        stackCards.push({ ...c })
                    }
                })
            }
        })
        setOpponentStackCards(stackCards)
        commonDataRef.current.opponentStackCards = stackCards
    }, [opponentsData])

    useEffect(() => {
        let all = [...cardsLinks]
        opponentsDisplayOrder.forEach((p) => {
            if (opponentsData[p]) {
                all = [...all, ...opponentsData[p].cardsLinks]
            }
        })
        setAllCardsLinks(all)
    }, [cardsLinks, opponentsData])

    useEffect(() => {
        if (!playerDataRef.current.profileData.stillLoading) {
            let tmp = { ...playerDataRef.current }
            tmp.cardsLinks = [...cardsLinks]
            setPlayerData(tmp)
        }
    }, [cardsLinks])

    useEffect(() => {
        commonDataRef.current.playerTurnOrder = playerTurnOrder

        if (playerIdRef.current) {
            let oppOrder = []
            let index = playerTurnOrder.findIndex(a => a == playerIdRef.current)
            if (index >= 0) {
                if (index + 1 < playerTurnOrder.length) {
                    oppOrder = [...oppOrder, ...playerTurnOrder.slice(index + 1)]
                }
                oppOrder = [...oppOrder, ...playerTurnOrder.slice(0, index)]
            }
            setOpponentsDisplayOrder(oppOrder)
        }

        if (playerTurnOrder.length > 0) {
            setHost(playerTurnOrder[0])
        }
    }, [playerTurnOrder])

    useEffect(() => {
        if (gameOptions) {
            let pData = { ...playerDataRef.current }
            const newCounters = []
            gameOptions.format.countersStartingValues.forEach((c) => {
                newCounters.push({
                    id: uuidv4(),
                    value: c
                })
            })
            pData.playerCounters = newCounters
            pData.playerNotes = ""
            if (gameOptions.format.defaultNotes) {
                pData.playerNotes = gameOptions.format.defaultNotes
            }
            pData.playerTokens = {}

            // First player token
            if (gameOptions.format.newTurn.sharedTurn) {
                let tokenName = gameOptions.format.newTurn.firstPlayerTokenName
                if (tokenName) {
                    if (playerIdRef.current == currentPlayer) {
                        pData.playerTokens[tokenName] = true
                    }
                }
            }

            setTokensAvailable(gameOptions.format.tokens)

            playerDataRef.current = pData
            setPlayerData(pData)
            setHistory([])
            setIsEliminated(false)
            setIsPausingBeforeUpkeep({ val: false, lastTurn: -1 })
            commonDataRef.current.history = []

            if (isDebug) {
                setOpponentsData(opponentDataDebug)
                opponentsDataRef.current = opponentDataDebug
                setOpponentsDisplayOrder(displayOrderDebug)
                setPlayerTurnOrder([
                    playerIdRef.current,
                    ...displayOrderDebug
                ])
            }
        }
    }, [gameOptions])

    useEffect(() => {
        let elminatedCount = 0
        opponentsDisplayOrder.forEach((p) => {
            if (opponentsData[p] && opponentsData[p].isEliminated) {
                elminatedCount += 1
            }
        })
        if (isEliminated) {
            elminatedCount += 1
        }

        if (isYourTurn && isEliminated && playerTurnOrder.length > elminatedCount) {
            endTurn()
        }
        commonDataRef.current.turnCount = turnCount
        commonDataRef.current.currentPlayer = currentPlayer
    }, [turnCount])

    /*------------------------------- MULTIPLAYER -------------------------------*/

    const closeCall = () => {
        console.log(preventReconnection.current)
        preventReconnection.current = true
        //console.trace('closing call');
        playersRef.current.forEach(p => {
            if (p.connection) {
                p.connection.send?.({
                    type: "DISCONNECT_PEER",
                    targetId: playerIdRef.current
                });
                p.connection.close()
            }
        });

        peerInstance.current?.connections?.forEach?.(connsArray => {
            connsArray.forEach(conn => {
                if (conn.open) {
                    try {
                        conn.close();
                    } catch (e) {
                        console.warn("Erreur fermeture connexion entrante", e);
                    }
                }
            });
        });

        if (peerInstance.current && typeof peerInstance.current.destroy === "function") {
            try {
                peerInstance.current.disconnect();
                peerInstance.current.destroy();
            } catch (e) {
                console.warn("Erreur lors du destroy()", e);
            }
        }

        playersRef.current = [];
        peerInstance.current = null;

        setTimeout(() => {
            navigate('/'); // Navigates relative to the current route
        }, 100)
    }

    function getPlayerName(peerId) {
        if (opponentsDataRef.current && opponentsDataRef.current[peerId]) {
            return opponentsDataRef.current[peerId].profileData.name
        }
        return peerId
    }

    const kickPlayer = (playerId) => {
        playersRef.current.forEach(p => {
            p.connection?.send?.({
                type: "DISCONNECT_PEER",
                targetId: playerId
            });
        });
        sendToHistory(getPlayerName(playerId) + " has been kicked", false, HISTORY_MESSAGE_TYPE.GLOBAL)

        disconnectWith(playerId)
    };

    const disconnectWith = (playerId) => {
        // Ignore si déjà en train de se déconnecter
        if (disconnectingIds.has(playerId)) return;
        disconnectingIds.add(playerId);

        if (playerId === playerIdRef.current) {
            // C'est moi qui suis kick
            sendToHistory("You have been kicked", false, HISTORY_MESSAGE_TYPE.GLOBAL, true)
            setPlayerTurnOrder([playerId]) // A fix
            closeCall()
        }

        // Fermer la connection locale (si elle existe encore)
        const player = playersRef.current.find(p => p.id === playerId);
        if (player?.connection?.open) {
            player.connection._manuallyClosed = true
            player.connection.close();
        }

        // Supprimer le joueur de la liste
        //playersRef.current = playersRef.current.filter(p => p.id !== playerId);
        aPlayerHasLeftTheGame(playerId)

        // Nettoyage
        setTimeout(() => {
            disconnectingIds.delete(playerId);
        }, 2000);
    }

    const handleNewConnection = (conn, isIncoming = false, isFromCallButton = false) => {
        conn.on("open", () => {
            const pendingIndex = pendingCalls.current.findIndex(a => conn.peer === a)
            if (pendingIndex !== -1) {
                pendingCalls.current.splice(pendingIndex, 1)
            }

            const connectionIndex = playersRef.current.findIndex(a => a.id === conn.peer);
            const alreadyConnected = connectionIndex !== -1
            if (alreadyConnected) {
                const oldConn = playersRef.current[connectionIndex].connection
                // Connexion redondante, on ignore l'ajout mais on peut échanger des données
                oldConn._manuallyClosed = true
                oldConn.close()
                playersRef.current[connectionIndex].connection = conn
                sendToHistory("New connection with " + getPlayerName(conn.peer), false, HISTORY_MESSAGE_TYPE.GLOBAL, true)
                sendData({
                    syncPlayerTurnOrder: commonDataRef.current.playerTurnOrder,
                    playerData: playerDataRef.current,
                }, conn);
                return;
            }

            if (isIncoming) {
                // On se connecte à nous
                if (playersRef.current.length >= 4) {
                    conn.send({ errorMessage: "You have been kicked: server full" });
                    conn.close();
                    sendToHistory("Connection refused: server full", false, HISTORY_MESSAGE_TYPE.ERROR);
                    return;
                }

                // A voir si vraiment necessaire ca
                if (!commonDataRef.current.playerTurnOrder.includes(conn.peer)) {
                    commonDataRef.current.playerTurnOrder.push(conn.peer);
                    setPlayerTurnOrder([...commonDataRef.current.playerTurnOrder]);
                }
            }

            // Si il nous rejoins alors qu'une partie est déjà en cours et qu'on est l'hote, on lui envoie ses données
            if (commonDataRef.current.gameOptions && commonDataRef.current.playerTurnOrder[0] === playerIdRef.current) {
                sendGameDataToReconnection(conn)
            }

            playersRef.current.push({ connection: conn, id: conn.peer });
            lastSeenMap.current[conn.peer] = Date.now();

            if (isIncoming) {
                sendData({
                    playerTurnOrder: commonDataRef.current.playerTurnOrder,
                    playerData: playerDataRef.current,
                });
            } else {
                // On se connecte à quelqu'un
                sendData({
                    playerData: playerDataRef.current,
                    selectedGame: gameData.name,
                });
            }

            if (isFromCallButton) {
                setTimeout(() => {
                    sendToHistory(playerData.profileData.name + " joined the game", false, HISTORY_MESSAGE_TYPE.GLOBAL);
                }, 1000);
            }
        });

        conn.on("close", () => {
            if (conn._manuallyClosed) { return }

            console.log(`Connexion fermée avec ${conn.peer}, en attente d'une éventuelle reconnexion...`);
            attemptReconnect(conn.peer);
        });

        conn.on("error", err => {
            console.error("Erreur peerjs", err.type, err)
            sendToHistory(`Connection Error with ${getPlayerName(conn.peer)}`, false, HISTORY_MESSAGE_TYPE.ERROR, true);
            attemptReconnect(conn.peer);
        });

        conn.on("data", (data) => {
            if (data.type === "ping") {
                updateLastSeen(conn.peer);
                conn.send({ type: "pong" });
            } else if (data.type === "pong") {
                updateLastSeen(conn.peer);
            } else if (data?.type === 'DISCONNECT_PEER' && data.targetId) {
                disconnectWith(data.targetId)
            } else {
                handleRecievedData(data, conn.peer);
            }
        });
    };

    const attemptReconnect = (peerId, retries = 5, delay = 1000) => {
        if (!playersRef.current.some(a => a.id === peerId)) {
            reconnectingPeers.delete(peerId);
            return
        }
        if (retries <= 0) {
            reconnectingPeers.delete(peerId);
            console.warn(`Échec final de reconnexion à ${peerId}`);
            //disconnectWith(peerId); A voir lequel est le mieux
            aPlayerHasLeftTheGame(peerId);
            sendToHistory(`Connection lost with ${getPlayerName(peerId)}`, false, HISTORY_MESSAGE_TYPE.GLOBAL, true);
            return;
        }

        if (reconnectingPeers.has(peerId)) {
            console.log(`Reconnexion déjà en cours pour ${peerId}, on ignore`);
            return;
        }

        reconnectingPeers.add(peerId);
        sendToHistory(`Reconnecting with ${getPlayerName(peerId)} - tries left ${retries}`, false, HISTORY_MESSAGE_TYPE.GLOBAL, true);

        setTimeout(() => {
            if (!peerInstance.current) { return }
            const conn = peerInstance.current.connect(peerId);
            let reconnected = false;

            const pongTimeout = setTimeout(() => {
                console.log(`Pas de pong reçu de ${peerId}, fermeture de la connexion`, false, false, true);
                conn.close();
                reconnectingPeers.delete(peerId);
                if (!reconnected) {
                    setTimeout(() => {
                        attemptReconnect(peerId, retries - 1, delay * 1.5);
                    }, delay);
                }
            }, 1000);

            conn.once("open", () => {
                conn.send({ type: "ping" });
            })

            conn.on("data", (data) => {
                if (data.type === "pong") {
                    clearTimeout(pongTimeout);
                    conn.removeAllListeners("data");
                    conn.removeAllListeners("open");
                    conn.removeAllListeners("error");
                    conn.removeAllListeners("close");
                    reconnectingPeers.delete(conn.peer);
                    reconnected = true;
                    handleNewConnection(conn, false, false);
                    const connectionIndex = playersRef.current.findIndex(a => a.id === conn.peer);
                    if (connectionIndex !== -1) {
                        const oldConn = playersRef.current[connectionIndex].connection
                        // Connexion redondante, on ignore l'ajout mais on peut échanger des données
                        oldConn._manuallyClosed = true
                        oldConn.close()
                        playersRef.current[connectionIndex].connection = conn
                        updateLastSeen(conn.peer)
                    }
                    if (commonDataRef.current.gameOptions) {
                        sendGameDataToReconnection(conn)
                    }
                    sendToHistory(`Reconnection successfull with ${getPlayerName(conn.peer)}`, false, HISTORY_MESSAGE_TYPE.GLOBAL, true)
                }
            });

            const failReconnect = () => {
                if (!reconnected) {
                    reconnectingPeers.delete(peerId);
                    setTimeout(() => {
                        attemptReconnect(peerId, retries - 1, delay * 1.5);
                    }, delay);
                }
            };

            conn.once("error", failReconnect);
            conn.once("close", failReconnect);
        }, delay);
    };

    const createPeerWithFallback = (desiredId, config) => {
        if (!navigator.onLine) {
            sendToHistory("You are offline", false, HISTORY_MESSAGE_TYPE.ERROR, true)
            return null
        }
        return new Promise((resolve, reject) => {
            const tryPeer = new Peer(desiredId, config);

            tryPeer.on('open', (id) => {
                console.log("Peer connected with ID:", id);
                resolve(tryPeer);
            });

            tryPeer.on('error', (err) => {
                if (err.type === 'unavailable-id') {
                    console.warn(`ID ${desiredId} already taken. Using fallback random ID.`);
                    const fallbackPeer = new Peer(undefined, config);

                    fallbackPeer.on('open', (id) => {
                        console.log("Fallback Peer ID:", id);
                        resolve(fallbackPeer);
                    });

                    fallbackPeer.on('error', (err2) => {
                        console.error("Fallback peer error:", err2);
                        reject(err2);
                    });
                } else {
                    reject(err);
                }
            });
        });
    };

    const iniPeer = async (playerId) => {
        const config = {
            host: "tcg-arena.fr",
            port: 443,
            path: "/myapp",
            secure: true,
            config: {
                iceServers: [
                    {
                        urls: 'stun:tcg-arena.fr:3478'
                    },
                    // Ton serveur TURN
                    {
                        urls: [
                            'turn:tcg-arena.fr:3478?transport=udp',
                            'turn:tcg-arena.fr:3478?transport=tcp',
                            'turn:tcg-arena.fr:5349?transport=tcp'
                        ],
                        username: 'turnuser',
                        credential: 'turnuser123456'
                    }
                ],
            }
        };
        peerInstance.current = "initializing..."

        try {
            const peer = await createPeerWithFallback(playerId, config);
            if (!peer) { return }
            playerIdRef.current = peer.id;
            peerInstance.current = peer;

            playersRef.current.push({ connection: null, id: peer.id });
            setPlayerTurnOrder([peer.id]);
            commonDataRef.current.playerTurnOrder = [peer.id];

            peer.on("connection", (conn) => {
                handleNewConnection(conn, true);
            });

            peer.on("disconnected", () => {
                if (!preventReconnection.current) {
                    sendToHistory("Disconnected, trying to reconnect", false, HISTORY_MESSAGE_TYPE.GLOBAL, true)
                    peer.reconnect()
                }
            })

            peer.on("close", () => {
                console.log("Je suis bien détruis")
            })

            peer.on("error", err => {
                console.log("Error PeerJS", err)
            })

            setInterval(() => {
                playersRef.current.forEach(p => {
                    if (p.connection && p.connection.open) {
                        p.connection.send({ type: "ping" });
                    }
                });
            }, PING_INTERVAL);
        } catch (err) {
            console.error("Failed to initialize Peer:", err);
        }
    };


    const call = (peerId, isFromCallButton) => {
        if (peerId === peerInstance.current.id) {
            sendToHistory("You can't connect with yourself.", false, HISTORY_MESSAGE_TYPE.ERROR, true);
            return;
        }

        if (pendingCalls.current.includes(peerId)) {
            return
        }
        pendingCalls.current.push(peerId)

        if (playersRef.current.some(a => a.id === peerId)) { return }

        const conn = peerInstance.current.connect(peerId);
        handleNewConnection(conn, false, isFromCallButton);
    };

    function aPlayerHasLeftTheGame(playerId) {
        // Retire le joueur de la partie, plus moyen de tenter une reconnexion
        playersRef.current = playersRef.current.filter(p => p.id !== playerId);

        if (opponentsDataRef.current[playerId]) {
            sendToHistory(getPlayerName(playerId) + " has left the game", false, HISTORY_MESSAGE_TYPE.GLOBAL, true)
        }

        // Remove from turn order
        let tmpTurnOrder = [...commonDataRef.current.playerTurnOrder]

        let index = tmpTurnOrder.findIndex(a => a == playerId)
        if (index != -1) {
            tmpTurnOrder.splice(index, 1)
            setPlayerTurnOrder(tmpTurnOrder)
        }

        // Remove opponent data
        //let tmpOpponentsData = { ...opponentsDataRef.current }
        //delete tmpOpponentsData[playerId]
        //setOpponentsData(tmpOpponentsData)
    }

    function sendGameDataToReconnection(conn) {
        let reconnectionPlayerData = {}
        if (opponentsDataRef.current[conn.peer]) {
            //sendToHistory("Player found", false, false, true)
            reconnectionPlayerData = opponentsDataRef.current[conn.peer]
        } else {
            //sendToHistory("I HAVE NEVER SEEN THIS GUY BEFORE", false, false, true)
            reconnectionPlayerData.unknownPlayer = true
        }
        reconnectionPlayerData.turnCount = commonDataRef.current.turnCount
        reconnectionPlayerData.currentPlayer = commonDataRef.current.currentPlayer
        reconnectionPlayerData.stackOrder = commonDataRef.current.stackOrder
        reconnectionPlayerData.opponentStackCards = commonDataRef.current.opponentStackCards

        conn.send({
            playerTurnOrder: commonDataRef.current.playerTurnOrder,
            playerData: playerDataRef.current,
            reconnectRequest: true,
            gameOptions: commonDataRef.current.gameOptions,
            reconnectionPlayerData: reconnectionPlayerData
        });
    }

    const sendData = useCallback((data) => {
        playersRef.current.forEach((ref) => {
            if (ref.connection) {
                ref.connection.send(data);
            }
        });
    }, []);

    const handleRecievedData = (data, senderId) => {
        if (isDebug) { return }
        const connIndex = playersRef.current.findIndex(a => a.id == senderId)
        if (connIndex == -1) { console.log("Unknown sender"); return }
        if (data.errorMessage) {
            sendToHistory(data.errorMessage, false, HISTORY_MESSAGE_TYPE.ERROR, true)
        }
        if (data.selectedGame) {
            if (data.selectedGame != gameData.name) {
                const conn = playersRef.current[connIndex].connection
                conn.send({ errorMessage: `You have been kicked: you are playing ${data.selectedGame} and not ${gameData.name}. Change game in the main menu"` })
                conn.close()    // JE DOIS PROPAGER LA AUSSI OU PAS ?
                sendToHistory(`Player failed to join: playing ${data.selectedGame} instead of ${gameData.name}`, false, HISTORY_MESSAGE_TYPE.ERROR)
                return
            }
        }

        if (data.playerTurnOrder) {
            commonDataRef.current.playerTurnOrder = data.playerTurnOrder
            setPlayerTurnOrder([...data.playerTurnOrder])
        }
        if (data.syncPlayerTurnOrder) {
            const newOrder = data.syncPlayerTurnOrder;
            const selfId = playerIdRef.current;

            // Ajouter nous-même si on est absent
            if (!newOrder.includes(selfId)) {
                const updated = [...newOrder, selfId];
                commonDataRef.current.playerTurnOrder = updated;
                setPlayerTurnOrder(updated);

                // Broadcast à tous les pairs
                sendData({ playerTurnOrder: updated });
            } else {
                // Sync simple
                commonDataRef.current.playerTurnOrder = newOrder;
                setPlayerTurnOrder(newOrder);
            }
        }
        if (data.playerTurnOrder || data.syncPlayerTurnOrder) {
            const knownPeers = data.playerTurnOrder ? data.playerTurnOrder : data.syncPlayerTurnOrder
            knownPeers.forEach(peerId => {
                if (peerId !== playerIdRef.current && !playersRef.current.some(conn => conn.id === peerId) && peerId > playerIdRef.current) {
                    //sendToHistory(`Calling ${getPlayerName(peerId)}`, false, false, true)
                    //console.log(peerId, playersRef.current, playersRef.current.some(conn => conn.id === peerId))
                    call(peerId)
                }
            });
        }

        if (data.ping) {
            createPingAtCard(data.ping)
        }


        if (data.playerData) {
            let tmpOpponentsData = { ...opponentsDataRef.current }
            if (tmpOpponentsData[senderId]) {
                const newRefreshCount = tmpOpponentsData[senderId].refreshCount ? tmpOpponentsData[senderId].refreshCount + 1 : 1
                tmpOpponentsData[senderId] = { ...data.playerData, refreshCount: newRefreshCount < 1000 ? newRefreshCount : 1 }
            } else {
                tmpOpponentsData[senderId] = { ...data.playerData, refreshCount: 1 }
            }

            setOpponentsData(tmpOpponentsData)
            opponentsDataRef.current = tmpOpponentsData
        }

        if (data.newToHistory) {
            if (data.newToHistory.id != commonDataRef.current.history.at(-1)) {
                commonDataRef.current.history = [...commonDataRef.current.history, data.newToHistory]
                setHistory(commonDataRef.current.history)
            }
        }

        if (data.currentPlayer) {
            setCurrentPlayer(data.currentPlayer)
        }

        if (data.turnCount) {
            setTurnCount(data.turnCount)
            delete commonDataRef.current.endTurnInfo
            setHasPassed(false)
        }

        if (data.gameOptions) {
            // Player is rejoining
            if (commonDataRef.current.gameOptions && data.reconnectionPlayerData) {
                // Si reconnexion alors qu'on a deja nos données 
                //setCurrentPlayer(data.reconnectionPlayerData.currentPlayer)
                setZoomedPlayer(-1)
            } else {
                commonDataRef.current.gameOptions = { ...data.gameOptions }
                setGameOptions(commonDataRef.current.gameOptions)

                if (data.reconnectRequest) {
                    setIsReconnecting(true)
                    setTimeout(() => {
                        setIsReconnecting(false)
                    }, 2000)

                    const reconnectionPlayerData = data.reconnectionPlayerData
                    if (reconnectionPlayerData) {
                        // Delay because setgameoptions reset to 0 those data
                        setTimeout(() => {
                            let tmp = { ...playerDataRef.current }
                            if (reconnectionPlayerData.playerCounters) {
                                tmp.playerCounters = reconnectionPlayerData.playerCounters
                            }
                            if (reconnectionPlayerData.playerTokens) {
                                tmp.playerTokens = reconnectionPlayerData.playerTokens
                            }
                            if (reconnectionPlayerData.playerNotes) {
                                tmp.playerNotes = reconnectionPlayerData.playerNotes
                            }

                            setTurnCount(reconnectionPlayerData.turnCount)
                            setCurrentPlayer(reconnectionPlayerData.currentPlayer)
                            setStackOrder(reconnectionPlayerData.stackOrder)
                            setOpponentStackCards(reconnectionPlayerData.opponentStackCards)
                            playerDataRef.current = tmp
                            setPlayerData(tmp)
                        }, 100)

                        // On sauvegarde pour que playerSection puisse récupérer
                        localStorage.setItem("savedGameData", JSON.stringify(reconnectionPlayerData));
                    }
                }
            }
        }

        if (data.giveCard && data.giveCard.playerId == playerIdRef.current) {
            setGivenCard(data.giveCard.cards)
        }

        if (data.endTurnInfo) {
            commonDataRef.current.endTurnInfo = { ...data.endTurnInfo }
        }

        if (data.claimedToken) {
            let tmp = { ...playerDataRef.current }
            if (tmp.playerTokens[data.claimedToken]) {
                delete tmp.playerTokens[data.claimedToken]
                playerDataRef.current = tmp
                setPlayerData(tmp)
            }
        }

        if (data.stackOrder && commonDataRef.current.gameOptions) {
            if (commonDataRef.current.playerTurnOrder[0] !== playerIdRef.current) {
                //const newStackOrderDate = new Date(data.playerData.stackOrder.date)
                //sendToHistory("updating stack order from host ", false, false, true)
                /*const newData = {
                    date: newStackOrderDate,
                    order: data.playerData.stackOrder.order
                }*/
                const newData = [...data.stackOrder]
                commonDataRef.current.stackOrder = newData
                setStackOrder(newData)
            }
        }
    }

    const updateLastSeen = (peerId) => {
        lastSeenMap.current[peerId] = Date.now();
    };




    /*------------------------------- GAME FUNCTIONS -------------------------------*/

    const sendToHistory = useCallback((text, card, messageType, localOnly) => {
        const message = {
            id: uuidv4(),
            player: playerDataRef.current.profileData.name,
            text: text,
            card: card ? { ...card } : false,
            type: messageType
        };

        commonDataRef.current.history = [...commonDataRef.current.history, message];
        setHistory(commonDataRef.current.history);

        if (!localOnly) {
            sendData({
                newToHistory: message
            });
        }
    }, [setHistory, sendData]);


    function updateGameOptions(gameOptions) {
        commonDataRef.current.gameOptions = { ...gameOptions }
        setGameOptions(commonDataRef.current.gameOptions)
        sendData({
            gameOptions: gameOptions
        })
        let startingPlayerId = gameOptions.startingPlayer.id
        if (gameOptions.startingPlayer.id === "RANDOM_STARTING_PLAYER") {
            let randomIndex = Math.floor(Math.random() * playerTurnOrder.length);
            startingPlayerId = playerTurnOrder[randomIndex]
        }
        setPlayerTurnTo(startingPlayerId, true)
    }

    function endTurn() {
        if (gameOptions.format.newTurn.sharedTurn && playerTurnOrder.length != 1) {
            sendToHistory(playerData.profileData.name + " ended its turn", false, HISTORY_MESSAGE_TYPE.GLOBAL)
            let currentEndTurnInfo = commonDataRef.current.endTurnInfo

            if (currentEndTurnInfo) {
                if (currentEndTurnInfo.total == playerTurnOrder.length - 1) {
                    let newPlayer = commonDataRef.current.endTurnInfo.firtToPass
                    setTimeout(() => {
                        setPlayerTurnTo(newPlayer)
                    }, 100)
                    delete commonDataRef.current.endTurnInfo
                    setHasPassed(false)
                } else {
                    sendData({
                        endTurnInfo: {
                            total: currentEndTurnInfo.total + 1,
                            firtToPass: currentEndTurnInfo.firtToPass
                        }
                    })
                    setHasPassed(true)
                }
            } else {
                sendData({
                    endTurnInfo: {
                        total: 1,
                        firtToPass: playerIdRef.current
                    }
                })
                setHasPassed(true)
            }
        } else {
            nextTurn()
        }
    }

    function nextTurn() {
        let nextPlayer = playerTurnOrder[0]
        let index = playerTurnOrder.findIndex((e) => e == currentPlayer)
        if (index != -1) {
            index += 1
            if (index >= playerTurnOrder.length) {
                index = 0
            }
            nextPlayer = playerTurnOrder[index]
        }
        setPlayerTurnTo(nextPlayer)
    }

    function setPlayerTurnTo(playerId, isRestart) {
        setTurnCount(isRestart ? 1 : turnCount + 1)
        setCurrentPlayer(playerId)
        sendData({
            currentPlayer: playerId,
            turnCount: isRestart ? 1 : turnCount + 1
        })

        let playerName = playerData.profileData.name
        if (opponentsData[playerId]) {
            playerName = opponentsData[playerId].profileData.name
        }

        let turn = gameOptions ? (gameOptions.format.newTurn.sharedTurn ? (turnCount + 1) : (Math.ceil((turnCount + 1) / playerTurnOrder.length))) : 0
        if (isRestart) {
            turn = 1
        }
        sendToHistory(playerName + " starting turn " + turn, false, HISTORY_MESSAGE_TYPE.GLOBAL)
    }

    useEffect(() => {
        if (options.focusActivePlayer && gameOptions && !gameOptions.format.newTurn.sharedTurn) {
            setZoomedPlayer(opponentsDisplayOrder.findIndex(a => a === currentPlayer))
        }
    }, [currentPlayer])

    const createPingAtCard = useCallback((card) => {
        const el = document.getElementById(card.id + "-images");
        if (el) {
            const rect = el.getBoundingClientRect();

            const now = new Date();
            const newPing = {
                id: uuidv4(),
                time: now,
                x: rect.x + rect.width / 2,
                y: rect.y + rect.height / 2,
            };

            setPings((prevPings) => {
                const freshPings = [...prevPings, newPing].filter(p => now - p.time <= 2000);
                return freshPings;
            });
        }
    }, []);

    const pingCard = useCallback((card) => {
        sendData({
            ping: card,
        });
        createPingAtCard(card);
    }, [sendData, createPingAtCard]);

    const updatePlayerData = useCallback((data) => {
        if (!playerDataRef.current.profileData.stillLoading) {
            let tmp = JSON.parse(JSON.stringify(playerDataRef.current));
            tmp.visibleCards = [...data.visibleCards];
            tmp.deck = [...data.deck];
            tmp.deckTopCardHiddenTo = { ...data.deckTopCardHiddenTo };
            tmp.cardsLinks = [...cardsLinks];
            tmp.isEliminated = isEliminated
            //playerDataRef.current = tmp               A rajouter nan ?
            setPlayerData(tmp);
            console.log(peerInstance.current.id, playerIdRef.current);
            if (peerInstance.current.id === null) {
                //console.trace('id was set to null');
            }
        }
    }, [cardsLinks, setPlayerData, isEliminated]);

    const updateStackData = useCallback((data) => {
        //sendToHistory("sending new stack " + data.length, false, false, true)
        setStackOrder(data);
        commonDataRef.current.stackOrder = data
        sendData({
            stackOrder: data
        });
    }, [setStackOrder, sendData]);

    function changeCounterValue(counterIndex, newValue) {
        let pData = { ...playerDataRef.current }
        pData.playerCounters[counterIndex].value = newValue
        setPlayerData(pData)
    }

    function toggleToken(token) {
        let pData = { ...playerDataRef.current }
        if (pData.playerTokens[token.name]) {
            delete pData.playerTokens[token.name]
            sendToHistory("has lost the " + token.name + " token")
        } else {
            pData.playerTokens[token.name] = true
            if (token.isUnique) {
                sendData({
                    claimedToken: token.name
                })
            }
            sendToHistory("has taken the " + token.name + " token")
        }
        setPlayerData(pData)
    }

    function updateNotes(newNotes) {
        let pData = { ...playerDataRef.current }
        pData.playerNotes = newNotes
        setPlayerData(pData)
    }

    function toggleIsEliminated() {
        const newIsEliminated = !isEliminated
        if (newIsEliminated) {
            sendToHistory("Is eliminated")
        }
        setIsEliminated(newIsEliminated)

        let tmpData = { ...playerDataRef.current }
        tmpData.isEliminated = newIsEliminated
        playerDataRef.current = tmpData
        setPlayerData(tmpData)
    }

    const sendGiveCardData = useCallback(() => {
        if (cardToGiveBufferRef.current.length === 0) return;

        let destinationKeys = [];
        let destinationDict = {};

        cardToGiveBufferRef.current.forEach((data) => {
            if (destinationKeys.includes(data.destination)) {
                destinationDict[data.destination].push(data.card);
            } else {
                destinationKeys.push(data.destination);
                destinationDict[data.destination] = [data.card];
            }
        });

        destinationKeys.forEach((key) => {
            setTimeout(() => {
                sendData({
                    giveCard: {
                        cards: destinationDict[key],
                        playerId: key,
                    },
                });
            }, 100);
        });

        cardToGiveBufferRef.current = [];
    }, [sendData]);

    const giveCardTo = useCallback((card, destination) => {
        cardToGiveBufferRef.current.push({ card, destination });
        if (card.position.section === SECTIONS.EXILE_HIDDEN) {
            sendToHistory("has given card hidden");
        } else {
            sendToHistory("has given card", card);
        }
        setTimeout(() => {
            sendGiveCardData();
        }, 50);
    }, [sendToHistory, sendGiveCardData]);

    const removeTemporaryTokens = useCallback(() => {
        if (tokensAvailable) {
            let tmp = { ...playerDataRef.current };
            let hasChanged = false;
            tokensAvailable.forEach((t) => {
                if (tmp.playerTokens[t.name] && t.leavesAtEndOfTurn) {
                    delete tmp.playerTokens[t.name];
                    hasChanged = true;
                }
            });
            if (hasChanged) {
                playerDataRef.current = tmp;
                setPlayerData(tmp);
            }
        }
    }, [tokensAvailable, setPlayerData]);

    const createLinkTo = (destintation, section) => {
        let linkData = currentActionMode.actionData
        linkData.to = destintation
        linkData.toSection = section
        let tmp = [...cardsLinks]
        tmp.push(linkData)
        setCardsLinks(tmp)
        if (linkData.isFromQuickAction) {
            setCurrentActionMode({
                ...currentActionMode,
                action: ACTION_MODES.NONE,
                actionData: false
            })
        } else {
            setCurrentActionMode({
                ...currentActionMode,
                action: ACTION_MODES.LINK_FROM,
                actionData: false
            })
        }
    }

    if (!peerInstance.current) { return }

    return (
        <Stack className={"game position-relative" + (options ? (options.visiblePlayArea ? "" : " hide-play-area") : "") + (gameOptions && gameOptions.format.newTurn.sharedTurn ? " shared-turn" : "")} style={{ height: "100vh" }} direction="horizontal">
            <div style={{ width: "4rem" }} className="h-100 d-flex flex-column justify-content-between align-items-center left-bar">
                <div style={{ visibility: gameOptions ? "visible" : "hidden" }}>
                    <p>Turn {gameOptions ? gameOptions.format.newTurn.sharedTurn ? (turnCount) : (Math.ceil(turnCount / playerTurnOrder.length)) : ""}</p>
                    <LeftBarButton variant="primary" className={(isYourTurn && !hasPassed ? "visible" : "hidden") + " turn-" + turnCount + " players-" + playerTurnOrder.length + (hasPassed ? " passed" : "")} onClick={() => { endTurn() }} tooltip="End your turn (space)"><img src={require('../Commons/Assets/Icons/right-arrow.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className={(isPausingBeforeUpkeep.val ? "enabled" : "")} onClick={() => { setIsPausingBeforeUpkeep({ val: !isPausingBeforeUpkeep.val, lastTurn: turnCount + 1 }) }} tooltip="Pause before drawing on your next turn. Click again to continue your turn."><img src={require('./Assets/pause.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className={(isManualMode ? "enabled" : "")} onClick={() => { setIsManualMode(!isManualMode) }} tooltip="Disable auto untap and draw"><img src={require('./Assets/manual-mode.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className={(isEliminated ? "enabled" : "")} onClick={() => { toggleIsEliminated() }} tooltip="Toggle eliminated"><img src={require('./Assets/eliminated.png')} /></LeftBarButton>
                </div>
                <div className="d-flex flex-column">
                    <LeftBarButton variant="primary" onClick={() => { setShowMultiplayerPanel(true); setIsTextInputFocused(true) }} tooltip="Connect with other players and manage turn order"><img src={require('../Commons/Assets/Icons/connect.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" onClick={() => { setShowFormatPickerModal(true) }} tooltip="Start a new game"><img src={require(gameOptions ? './Assets/refresh.png' : './Assets/play.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" onClick={() => { setShowPreferencePanel(true); setIsTextInputFocused(true) }} tooltip="Preferences"><img src={require('../Commons/Assets/Icons/settings.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" onClick={() => { setShowHelpModal(true) }} tooltip="Help"><img src={require('../Commons/Assets/Icons/question-mark.png')} /></LeftBarButton>
                </div>
                <div className="d-flex flex-column">
                    <LeftBarButton variant="primary" onClick={() => { setShowDiceModal(true) }} tooltip="Roll a dice"><img src={require('./Assets/dice.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className="presented-toggle-button hidden" onClick={() => { setToggleRemovePresented(!toggleRemovePresented) }} tooltip="Cancel all forwards cards"><img src={require('./Assets/remove-presented.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className={gameOptions && cardsLinks.length > 0 ? "visible" : "hidden"} onClick={() => { setCardsLinks([]) }} tooltip="Delete all your card links"><img src={require('./Assets/cards-link-remove.png')} /></LeftBarButton>
                    <LeftBarButton variant="primary" className={gameOptions ? "visible" : "hidden"} onClick={() => { setShowTokensPanel(true); setIsTextInputFocused(true) }} tooltip="Create token"><img src={require('./Assets/create-token.png')} /></LeftBarButton>
                </div>
                <div>
                    <LeftBarButton variant="primary" onClick={() => setShowExitModal(true)} tooltip="Exit"><img src={require('./Assets/exit.png')} /></LeftBarButton>
                </div>
            </div>
            <div className="d-flex flex-column h-100 w-100 position-relative" style={{ paddingRight: "32vh" }}>
                {playerTurnOrder.length <= 1 && (
                    <div className="position-relative h-50 w-100 d-flex align-items-center justify-content-center">
                        <div className='box-container position-relative align-items-center justify-content-center w-50 h-50'>
                            {!gameOptions && (<p>Use the <img src={require('../Commons/Assets/Icons/connect.png')} /> button to connect with other players</p>)}
                            {gameOptions && (<p>Game has started, you can't join other players but they can still join you.</p>)}
                        </div>
                    </div>
                )}
                {isReconnecting && (<div className="position-absolute h-50 w-100 d-flex align-items-center justify-content-center" style={{ paddingRight: "32vh" }}><Spinner animation="border" role="status" /></div>)}
                {playerTurnOrder.length > 1 && (
                    <div className={"opponents position-relative h-50 w-100 total-" + (opponentsDisplayOrder.length)} style={{ opacity: isReconnecting ? 0 : 1 }} >
                        {opponentsDisplayOrder.map((p, i) => {
                            if (p !== playerIdRef.current && opponentsData[p]) {
                                return (
                                    <OpponentSection
                                        key={p}
                                        opponentId={p}
                                        opponentData={opponentsData[p]}
                                        setFocusedCard={setFocusedCard}
                                        pingCard={pingCard}
                                        stackRef={stackRef}
                                        isCurrentPlayer={currentPlayer == p}
                                        currentActionMode={currentActionMode}
                                        setCurrentActionMode={setCurrentActionMode}
                                        cardsLinks={cardsLinks}
                                        setCardsLinks={setCardsLinks}
                                        profileData={opponentsData[p].profileData}
                                        gameOptions={gameOptions}
                                        playerId={playerIdRef.current}
                                        sendToHistory={sendToHistory}
                                        placeInDisplayOrder={i}
                                        currentlyZoomedPlayer={zoomedPlayer}
                                        opponentsTotal={opponentsDisplayOrder.length}
                                    />)
                            }
                        })}
                    </div>
                )}
                <PlayerSection
                    sendData={updatePlayerData}
                    turnCount={turnCount}
                    isYourTurn={isActivePlayer}
                    setFocusedCard={setFocusedCard}
                    pingCard={pingCard}
                    stackRef={stackRef}
                    opponentStackCards={opponentStackCards}
                    sendToHistory={sendToHistory}
                    opponentIds={opponentsDisplayOrder}
                    opponentsData={opponentsData}
                    givenCard={givenCard}
                    setGivenCard={setGivenCard}
                    giveCardTo={giveCardTo}
                    showTokensModal={showTokensPanel}
                    setShowTokensModal={setShowTokensPanel}
                    currentActionMode={currentActionMode}
                    setCurrentActionMode={setCurrentActionMode}
                    cardsLinks={cardsLinks}
                    setCardsLinks={setCardsLinks}
                    profileData={playerData.profileData}
                    gameOptions={gameOptions}
                    toggleRemovePresented={toggleRemovePresented}
                    playerId={playerIdRef.current}
                    options={options}
                    removeTemporaryTokens={removeTemporaryTokens}
                    setIsTextInputFocused={setIsTextInputFocused}
                    stackOrder={stackOrder}
                    updateStackOrder={updateStackData}
                    isPausingBeforeUpkeep={isPausingBeforeUpkeep}
                    isEliminated={isEliminated}
                    isManualMode={isManualMode}
                    host={host}
                />
                <History history={history} sendToHistory={sendToHistory} setIsTextInputFocused={setIsTextInputFocused} playerId={playerIdRef.current} />
            </div>
            <div className='pe-none position-fixed top-0 start-0 w-100 h-100' style={{ zIndex: 90000 }}>
                {pings.map((ping) => {
                    return (<div className='position-fixed ping translate-middle' key={ping.id} style={{ left: ping.x, top: ping.y }}></div>)
                })}
            </div>
            <div className="d-flex flex-column position-absolute top-0 end-0">
                {opponentsDisplayOrder.map((p, i) => {
                    if (p != playerIdRef.current && opponentsData[p]) {
                        return (<PlayerCountersOpponent
                            profileData={opponentsData[p].profileData}
                            counters={opponentsData[p].playerCounters}
                            opponentIndex={i}
                            tokens={opponentsData[p].playerTokens}
                            tokensAvailable={tokensAvailable}
                            notes={opponentsData[p].playerNotes}
                            setIsTextInputFocused={setIsTextInputFocused}
                            key={p}
                            refreshCount={opponentsData[p].refreshCount}
                            playerId={p}
                            actionMode={currentActionMode}
                            createLinkTo={createLinkTo}
                        />)
                    }
                })}
            </div>
            <div className="position-absolute bottom-0" style={{ left: "4.1rem" }}>
                <PlayerCountersPlayer
                    profileData={playerData.profileData}
                    counters={playerData.playerCounters}
                    changeCounterValue={changeCounterValue}
                    tokens={playerData.playerTokens}
                    tokensAvailable={tokensAvailable}
                    toggleToken={toggleToken}
                    notes={playerData.playerNotes}
                    updateNotes={updateNotes}
                    setIsTextInputFocused={setIsTextInputFocused}
                    playerId={playerIdRef.current}
                    actionMode={currentActionMode}
                    createLinkTo={createLinkTo}
                />
            </div>
            {/*<div style={{width: "35vh"}} className="h-100"></div>*/}
            <ActionMode
                actionMode={currentActionMode}
                setActionMode={setCurrentActionMode}
                zoomedPlayer={zoomedPlayer}
                setZoomedPlayer={setZoomedPlayer}
                isTextInputFocused={isTextInputFocused}
                endTurn={() => {
                    if (isYourTurn && !hasPassed) {
                        endTurn()
                    }
                }}
                opponentsTotal={opponentsDisplayOrder.length}
            />
            <CardsLinks links={allCardsLinks} />
            <FocusedCard focusedCard={focusedCard} options={options} />
            <NewTurnMessage turnCount={turnCount} opponentsData={opponentsData} currentPlayer={currentPlayer} gameOptions={gameOptions} />
            <MultiplayerModal
                show={showMultiplayerPanel}
                handleClose={handleCloseMultiplayer}
                peerInstance={peerInstance}
                playerId={playerIdRef.current}
                call={call}
                playerTurnOrder={playerTurnOrder}
                setPlayerTurnOrder={setPlayerTurnOrder}
                opponentsData={opponentsData}
                gameOptions={gameOptions}
                selectedGame={gameData.name}
                playerName={playerData.profileData.name}

                playerData={playerData}
                sendData={sendData}
                currentPlayer={currentPlayer}
                becomeActivePlayer={() => setPlayerTurnTo(playerIdRef.current)}
                disconnectWith={kickPlayer}
            />
            <PreferenceModal
                show={showPreferencePanel}
                handleClose={handleClosePreference}
                playerData={playerData}
                setPlayerData={setPlayerData}
                options={options}
                setOptions={setOptions}
            />
            <DiceModal
                show={showDiceModal}
                handleClose={handleCloseDice}
                sendToHistory={sendToHistory}
            />
            <HelpModal
                show={showHelpModal}
                handleClose={handleCloseHelp}
                gameData={gameData}
            />
            <ExitModal
                show={showExitModal}
                handleClose={handleCloseExit}
                closeCall={closeCall}
            />
            <FormatPickerModal
                show={showFormatPickerModal}
                handleClose={handleCloseFormatPicker}
                gameData={gameData}
                playerTurnOrder={playerTurnOrder}
                opponentsData={opponentsData}
                playerData={playerData}
                peerInstance={peerInstance}
                setGameOptions={updateGameOptions}
                gameOptions={gameOptions}
            />
        </Stack>
    )
}